import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import {
  ACCENT_PALETTES,
  DEFAULT_ACCENT,
  applyAccentPalette,
  getStoredAccentPalette,
  type AccentPaletteId,
} from '../lib/accentPalette';

/**
 * AccentPalettePicker — the 5-swatch APP accent picker rendered inside the
 * UserMenu. Selecting a swatch instantly re-skins the app chrome (sets
 * `<html data-accent="…">`, which re-maps the accent token ramp app-wide —
 * see src/lib/accentPalette.ts + the `[data-accent]` block in index.css) and
 * persists the choice to localStorage. Default = Teal = the current Aurora
 * accent (carries no attribute), so existing users see no change.
 *
 * Each swatch is painted from the SAME `--cyan-500` token the palette
 * re-maps — the button carries its own `data-accent` so the token resolves to
 * that palette's hue WITHOUT any hardcoded hex in this component (keeps the
 * design-tokens sentinel happy + guarantees the swatch matches the live skin).
 */
export function AccentPalettePicker() {
  const [selected, setSelected] = useState<AccentPaletteId>(getStoredAccentPalette);

  // Re-assert the stored accent on mount (in case the menu opens before the
  // boot-time apply ran, e.g. SSR-less first paint in a fresh tab).
  useEffect(() => {
    applyAccentPalette(selected);
  }, [selected]);

  const choose = (id: AccentPaletteId) => {
    setSelected(id);
    applyAccentPalette(id);
  };

  return (
    <div
      className="px-2 py-1.5"
      role="radiogroup"
      aria-label="App accent palette"
      data-testid="accent-palette-picker"
    >
      <div className="text-[10px] font-mono uppercase tracking-wider text-text-muted mb-1.5">
        Accent
      </div>
      <div className="flex items-center gap-1.5">
        {ACCENT_PALETTES.map((p) => {
          const isActive = p.id === selected;
          // The default (teal) swatch must show its colour too — it carries
          // data-accent="teal" purely so the scoped --cyan-500 resolves to the
          // teal default; applyResolvedAccent still strips the attribute off
          // <html> for the default, so the app DOM stays byte-identical.
          return (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={isActive}
              aria-label={`${p.name}${p.id === DEFAULT_ACCENT ? ' (default)' : ''} — ${p.description}`}
              title={`${p.name} — ${p.description}`}
              data-accent={p.id}
              data-testid={`accent-swatch-${p.id}`}
              onClick={(e) => {
                e.preventDefault();
                choose(p.id);
              }}
              className={[
                'relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                'transition-[box-shadow,transform] hover:scale-110',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1',
                isActive ? 'ring-2 ring-text-primary ring-offset-1 ring-offset-surface-1' : '',
              ].join(' ')}
              style={{ backgroundColor: 'var(--cyan-500)' }}
            >
              {isActive && (
                <Check
                  className="h-3.5 w-3.5"
                  // Use the palette's on-accent foreground token so the tick
                  // reads on every hue (dark hues → light tick, etc.).
                  style={{ color: 'var(--primary-foreground)' }}
                  strokeWidth={3}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
